import React, { useEffect, useRef, useState } from "react";
import styles from "../../FilePreviewModal.module.scss";

interface EpubReaderProps {
  url: string;
  fileName?: string; // Optional prop for custom file name
}

const EpubReader: React.FC<EpubReaderProps> = ({ url, fileName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [epubRendition, setEpubRendition] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Extract filename from URL if not provided
  const getFileName = () => {
    if (fileName) return fileName;
    
    // Extract filename from URL
    const urlParts = url.split('/');
    let extractedName = urlParts[urlParts.length - 1];
    
    // Remove query parameters if present
    extractedName = extractedName.split('?')[0];
    
    // Make sure it has .epub extension
    if (!extractedName.toLowerCase().endsWith('.epub')) {
      extractedName += '.epub';
    }
    
    return extractedName;
  };

  useEffect(() => {
    if (!containerRef.current) return;
    
    let book: any = null;
    setIsLoading(true);
    setError(null);
    
    const loadEpub = async () => {
      try {
        // Just use require instead of dynamic import
        const ePub = require('epubjs').default;
        
        // Create book
        book = ePub(url);
        
        // Create rendition
        const rendition = book.renderTo(containerRef.current, {
          width: '100%',
          height: '100%',
          flow: 'paginated'
        });
        
        // Display book
        await rendition.display();
        setEpubRendition(rendition);
        
        // Generate page count
        await book.ready;
        await book.locations.generate(1024);
        setTotalPages(book.locations.total);
        
        // Track location changes
        rendition.on('relocated', (location: any) => {
          setCurrentPage(location.start.location+1);
        });
        
        setIsLoading(false);
      } catch (err: any) {
        console.error("Error loading EPUB:", err);
        setError(err.message || "Failed to load EPUB file");
        setIsLoading(false);
      }
    };
    
    loadEpub();
    
    // Clean up
    return () => {
      if (book) {
        book.destroy();
      }
    };
  }, [url]);
  
  // Navigation
  const goToPrevPage = () => {
    if (epubRendition) epubRendition.prev();
  };
  
  const goToNextPage = () => {
    if (epubRendition) epubRendition.next();
  };
  
  // Download function
  const downloadEpub = async () => {
    try {
      // Create a new anchor element
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = getFileName();
      
      // Append to body, click and remove
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error("Download failed:", err);
      // Fallback to opening in new tab if download fails
      window.open(url, '_blank');
    }
  };

  return (
    <div className={styles.epubContainer}>
      <div ref={containerRef} className={styles.epubContent}></div>
      
      {error && (
        <div className={styles.epubError}>
          {error}
          <div className={styles.epubErrorButtons}>
            <button 
              onClick={() => window.open(url, '_blank')}
              className={styles.openButton}
            >
              Open in browser
            </button>
            <button 
              onClick={downloadEpub}
              className={styles.downloadButton}
            >
              Download
            </button>
          </div>
        </div>
      )}
      
      {!isLoading && !error && epubRendition && (
        <div className={styles.epubControls}>
          <div className={styles.epubNavigation}>
            <button 
              onClick={goToPrevPage} 
              className={styles.epubButton}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            
            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              onClick={goToNextPage} 
              className={styles.epubButton}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
          
          <button 
            onClick={downloadEpub}
            className={styles.downloadBtn}
            title="Download EPUB"
          >
            Download
          </button>
        </div>
      )}
      
      {isLoading && <div className={styles.loading}>Loading...</div>}
    </div>
  );
};

export default EpubReader;